import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/helpers/esm/regeneratorRuntime";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import { ProLayout } from "@ant-design/pro-components";
import { ErrorBoundary } from "../components/ErrorBoundary";
import React, { useContext, useEffect, useMemo, useRef } from "react";
import MicroAppContainer from "../main/components/MicroAppContainer";
import { useLayoutInit } from "../main/hooks/useLayoutInit";
import { useProLayoutProps } from "../main/hooks/useProLayoutProps";
import PanesTransition from "../main/components/PanesTransition";
import { routesArraySelector, sendMessage, useAppStore, useMicroAppStore } from "../store";
import { ThemeProvider } from "styled-components";
import { isEmpty } from "lodash-es";
import { matchRoutes, Outlet, useLocation } from "react-router-dom";
import GlobalBreadcrumb from "../components/GlobalBreadcrumb";
import ConfigProvider from "antd/es/config-provider";
import theme from "antd/es/theme";
import microApp, { removeDomScope } from "@micro-zoe/micro-app";
import LayoutTabs from "../main/components/Tabs";
import { SideModeStylizedTheme } from "../theme/sideModeStylize";
import { GlobalStyle } from "../theme/global";
import { MainStyle } from "../main/components/LayoutContentWrapper/styles";
import FooterSection from "../components/FooterSection";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var SingleLayout = function SingleLayout(props) {
  var _props$layoutConfig$p, _props$layoutConfig, _props$layoutConfig2, _props$layoutConfig2$;
  var _useLocation = useLocation(),
    pathname = _useLocation.pathname;
  var _useContext = useContext(ConfigProvider.ConfigContext),
    getPrefixCls = _useContext.getPrefixCls;
  var lastMicroAppInfo = useRef(void 0);
  var prefixCls = getPrefixCls();
  var microApps = useMicroAppStore(function (s) {
    return s.microApps;
  });
  var setStatus = useMicroAppStore(function (s) {
    return s.setStatus;
  });
  var flatPathMenus = useAppStore(function (s) {
    return s.flatPathMenus;
  });
  var flatPathRoutes = useAppStore(function (s) {
    return s.flatPathRoutes;
  });
  var routesPaths = useAppStore(routesArraySelector);
  var errorCaptureException = useAppStore(function (s) {
    return s.errorCaptureException;
  });
  var captureException = useAppStore(function (s) {
    var _s$monitorConfig;
    return (_s$monitorConfig = s.monitorConfig) === null || _s$monitorConfig === void 0 ? void 0 : _s$monitorConfig.captureException;
  });
  var globalBreadcrumb = useAppStore(function (s) {
    return s.globalBreadcrumb;
  });
  var themeToken = theme.useToken().token;
  var menuInit = !isEmpty(flatPathMenus);

  // 框架初始化
  useLayoutInit(props);

  // proLayout设置
  var proLayoutProps = useProLayoutProps(props, props.proLayoutConfig);
  var composeTheme = {
    prefixCls: prefixCls,
    proLayout: proLayoutProps.token,
    token: themeToken
  };
  var errorFallback = ((_props$layoutConfig$p = props.layoutConfig.paneErrorBoundary) === null || _props$layoutConfig$p === void 0 ? void 0 : _props$layoutConfig$p.fallback) || void 0;
  var onError = errorCaptureException || props.layoutConfig.errorCaptureException || captureException;
  var sendUnmountMessage = function sendUnmountMessage() {
    var _lastMicroAppInfo$cur;
    var name = (_lastMicroAppInfo$cur = lastMicroAppInfo.current) === null || _lastMicroAppInfo$cur === void 0 ? void 0 : _lastMicroAppInfo$cur.name;
    if (!name) {
      return;
    }
    sendMessage(name, {
      event: "setAppStatus",
      data: "unmount"
    }, {
      sync: true,
      callback: function callback() {
        return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
          return _regeneratorRuntime().wrap(function _callee$(_context) {
            while (1) switch (_context.prev = _context.next) {
              case 0:
                sendMessage(name, {
                  event: "setAppStatus",
                  data: null
                });
                _context.prev = 1;
                _context.next = 4;
                return microApp.unmountApp(name);
              case 4:
                setStatus(name, "uninitialized");
                _context.next = 10;
                break;
              case 7:
                _context.prev = 7;
                _context.t0 = _context["catch"](1);
                setStatus(name, "error");
              case 10:
              case "end":
                return _context.stop();
            }
          }, _callee, null, [[1, 7]]);
        }))();
      }
    });
  };

  // 由于路由变化时，子应用路由匹配失败，会导致子应用不再进行再度匹配，需要卸载，等待下次加载重新初始化
  var PageContent = useMemo(function () {
    var _matchRoute$route, _lastMicroAppInfo$cur2;
    var _ref = matchRoutes(routesPaths, pathname || "") || [],
      _ref2 = _slicedToArray(_ref, 1),
      matchRoute = _ref2[0];
    if (matchRoute !== null && matchRoute !== void 0 && (_matchRoute$route = matchRoute.route) !== null && _matchRoute$route !== void 0 && _matchRoute$route.redirect) {
      return null;
    }
    var realPathname = (matchRoute === null || matchRoute === void 0 ? void 0 : matchRoute.route.path) || pathname;
    var activatedMicroAppInfo = microApps.find(function (app) {
      var routeInfo = flatPathRoutes[realPathname] || {};
      var routePath = routeInfo.microAppPath || routeInfo.path;
      if (!routePath) {
        return false;
      }
      return routePath.startsWith(app.baseRoute);
    });
    var name = (_lastMicroAppInfo$cur2 = lastMicroAppInfo.current) === null || _lastMicroAppInfo$cur2 === void 0 ? void 0 : _lastMicroAppInfo$cur2.name;
    // 从子应用跳转到主应用，也卸载前一个
    if (!activatedMicroAppInfo && name) {
      // 延迟重新初始化，避免组件更新失败
      removeDomScope(true);
      sendUnmountMessage();
    }
    // 子应用互相跳转，卸载前一个
    if (activatedMicroAppInfo && name && name !== (activatedMicroAppInfo === null || activatedMicroAppInfo === void 0 ? void 0 : activatedMicroAppInfo.name)) {
      sendUnmountMessage();
    }
    lastMicroAppInfo.current = activatedMicroAppInfo;
    return activatedMicroAppInfo ? null : /*#__PURE__*/_jsx("div", {
      className: "sa-page-content",
      children: /*#__PURE__*/_jsx(ErrorBoundary, {
        onError: onError,
        fallback: errorFallback,
        children: /*#__PURE__*/_jsx(Outlet, {})
      }, pathname)
    });
  }, [pathname]);
  var showHeaderRender = ((_props$layoutConfig = props.layoutConfig) === null || _props$layoutConfig === void 0 ? void 0 : _props$layoutConfig.headerRender) && props.proLayoutConfig.layout === "side";
  useEffect(function () {
    return function () {
      sendUnmountMessage();
    };
  }, []);
  return /*#__PURE__*/_jsxs(ThemeProvider, {
    theme: composeTheme,
    children: [/*#__PURE__*/_jsx(GlobalStyle, {}), /*#__PURE__*/_jsx(SideModeStylizedTheme, {}), /*#__PURE__*/_jsx(ProLayout, _objectSpread(_objectSpread({}, proLayoutProps), {}, {
      children: menuInit ? /*#__PURE__*/_jsxs(MainStyle, {
        style: {
          overflow: "initial",
          display: "block"
        },
        children: [showHeaderRender ? (_props$layoutConfig2 = props.layoutConfig) === null || _props$layoutConfig2 === void 0 || (_props$layoutConfig2$ = _props$layoutConfig2.headerRender) === null || _props$layoutConfig2$ === void 0 ? void 0 : _props$layoutConfig2$.call(_props$layoutConfig2) : null, /*#__PURE__*/_jsx(PanesTransition, {
          children: /*#__PURE__*/_jsxs("div", {
            className: "sa-main-content",
            style: {
              overflow: "initial"
            },
            children: [/*#__PURE__*/_jsxs("div", {
              className: "sa-page-wrapper",
              style: {
                paddingTop: showHeaderRender ? 0 : composeTheme.token.paddingLG
              },
              children: [/*#__PURE__*/_jsx(LayoutTabs, _objectSpread({}, props)), globalBreadcrumb === false ? null : /*#__PURE__*/_jsx(GlobalBreadcrumb, {}), PageContent, /*#__PURE__*/_jsx("div", {
                className: "sa-page-content sa-sub-app-hook",
                style: {
                  display: !PageContent ? "block" : "none"
                },
                children: /*#__PURE__*/_jsx(MicroAppContainer, {
                  layoutConfig: props.layoutConfig
                })
              })]
            }), /*#__PURE__*/_jsx("div", {
              className: "sa-footer-container",
              children: /*#__PURE__*/_jsx(FooterSection, {
                renderFooter: props.layoutConfig.renderFooter
              })
            })]
          })
        })]
      }) : null
    }))]
  });
};
export default SingleLayout;